import config from "@Config/index";
import axios from "axios";
import urlJoin from "helpers/urljoin";
import { logger } from "helpers/logger";
import { GUMLET_BASE_URL } from "constants/index";

// const VIDEO_UPLOAD_CREDENTIALS = "https://api.cloudinary.com/v1_1/amberstudent/video/upload";

export const getInventoryReviews = async (id: string, options: any = {}, limit = 50) => {
  options.url_path = options.url_path || "api/v0/inventories";
  options.url = urlJoin(
    config.API_URL,
    options.url_path,
    id,
    `reviews?limit=${limit}&p=1&relevant=true&sort_key=review_date&sort_order=desc&status=approved&lang=${options.lang}`,
  );
  options.params = {};
  const res = await axios.get(options.url);
  return res?.data?.data?.result;
};

export const uploadToS3 = async (policyData: any, file: any, sendKey: any = false) => {
  const { policy_hash, url, public_url } = policyData;
  const data = new FormData();

  data.append("key", policy_hash.key);
  data.append("acl", policy_hash.acl);
  data.append("success_action_status", policy_hash.success_action_status);
  data.append("policy", policy_hash.policy);
  if (file.type.includes("video")) {
    data.append("Content-Type", policy_hash["Content-Type"]);
  }
  data.append("Cache-Control", policy_hash["Cache-Control"]);
  data.append("x-amz-credential", policy_hash["x-amz-credential"]);
  data.append("x-amz-algorithm", policy_hash["x-amz-algorithm"]);
  data.append("x-amz-date", policy_hash["x-amz-date"]);
  data.append("x-amz-signature", policy_hash["x-amz-signature"]);
  data.append("file", file);

  const response = await axios.post(url, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  if (response.status < 200 || response.status >= 300) {
    throw new Error("Image upload failed");
  }

  return sendKey ? policy_hash.key : public_url;
};

export const fetchUploadCredentials = async (filename: any) => {
  try {
    const response = await axios.get(`${config.API_URL}/inventories/upload_video_credentials`, {
      params: { file_name: filename },
      withCredentials: true,
    });
    return response.data.data;
  } catch (error) {
    logger.error("Error while fetching upload credentials:", error);
    throw new Error("Failed to fetch upload credentials.");
  }
};

export const getEmojiFromRating = (rating: number) => {
  let emoji = "";
  if (rating > 4.5) {
    emoji = "🤩";
  } else if (rating > 4) {
    emoji = "😍";
  } else if (rating > 3.5) {
    emoji = "😀";
  } else {
    emoji = "😐";
  }
  return emoji;
};
export const uploadReviewImages = async (fileName: any, options: any = {}, id: any) => {
  options.url_path = options.url_path || "api/v0/inventories";
  options.url = urlJoin(
    config.API_URL,
    options.url_path,
    String(id),
    `reviews/upload_image_credentials?file_name=${fileName}`,
  );
  const res = await axios.get(options.url, {
    withCredentials: true,
  });
  return res?.data?.data;
};

export const uploadImage = async (
  file: any,
  credentials: { url: any; policy_hash: any; public_url: any },
) => {
  try {
    const { url, policy_hash, public_url } = credentials;

    if (!url || !policy_hash) {
      throw new Error("Credentials data is missing URL or policy_hash");
    }

    // Prepare form data for S3 image upload
    const formData = new FormData();
    formData.append("key", policy_hash.key);
    formData.append("acl", policy_hash.acl);
    formData.append("success_action_status", policy_hash.success_action_status);
    formData.append("policy", policy_hash.policy);
    formData.append("Cache-Control", policy_hash["Cache-Control"]);
    formData.append("x-amz-credential", policy_hash["x-amz-credential"]);
    formData.append("x-amz-algorithm", policy_hash["x-amz-algorithm"]);
    formData.append("x-amz-date", policy_hash["x-amz-date"]);
    formData.append("x-amz-signature", policy_hash["x-amz-signature"]);
    formData.append("file", file);

    // Upload the image to S3
    const uploadResponse = await fetch(url, {
      method: "POST",
      body: formData,
    });

    return { uploadResponse, public_url };

    // Add the public URL to the state without duplicates
  } catch (error) {
    return error;
  }
};

export const uploadVideoToCloudinary = async (file: any) => {
  const res = await fetchUploadCredentials(file.name);
  try {
    const videoS3Url = await uploadToS3(res, file, true);

    if (videoS3Url) {
      return videoS3Url;
    }
    return null;
  } catch (error) {
    return error;
  }
};

export const updateCloudinaryUrl = (url: string, height: number, width: number) => {
  if (!url) return "";
  // Use a regular expression to find and replace height and width values in the URL
  const updatedUrl = url
    .replace(/h_\d+/g, height ? `h_${height}` : "$&") // Replace height if provided
    .replace(/w_\d+/g, width ? `w_${width}` : "$&"); // Replace width if provided

  return updatedUrl;
};

export const uploadReview = async (review: any, options: any, inventoryId: any) => {
  options.url_path = options.url_path || "api/v0/inventories";
  review["status"] = "pending";
  options.url = urlJoin(config.API_URL, options.url_path, String(inventoryId), "reviews");
  const res = await axios.post(
    options.url,
    {
      review,
    },
    {
      withCredentials: true,
    },
  );
  return res?.data?.data;
};

export const updateReviews = async (review: any, options: any, inventoryId: any, reviewId: any) => {
  options.url_path = options.url_path || "api/v0/inventories";
  review["status"] = "pending";
  options.url = urlJoin(
    config.API_URL,
    options.url_path,
    String(inventoryId),
    `reviews/${reviewId}`,
  );
  const res = await axios.put(
    options.url,
    {
      review,
    },
    {
      withCredentials: true,
    },
  );
  return res?.data?.data;
};

const patterns = [
  {
    regex: /v(\d+)\/inventories\/(.+)\.mp4(?:\?.*)?$/,
    transformer: (match: RegExpExecArray) =>
      `res/amberstudent/video/upload/inventories/${match[2]}/${match[1]}.mp4`,
  },
  {
    regex: /res\/amberstudent\/video\/upload\/inventories\/([^\/]+)\/(\d+)\.mp4(?:\?.*)?$/,
    transformer: (match: RegExpExecArray) =>
      `res/amberstudent/video/upload/inventories/${match[1]}/${match[2]}.mp4`,
  },
  {
    regex: /res\/videos\/([^\/]+)\.mp4(?:\?.*)?$/,
    transformer: (match: RegExpExecArray) => `res/videos/${match[1]}.mp4`,
  },
];
export const transformVideoPath = (videoUrl: string) => {
  if (!videoUrl) return videoUrl;
  const [basePath, queryParams] = videoUrl.split("?");
  for (const pattern of patterns) {
    const match = pattern.regex.exec(basePath);
    if (match) {
      const transformedPath = pattern.transformer(match);
      return `${GUMLET_BASE_URL}${transformedPath}${queryParams ? `?${queryParams}` : ""}`;
    }
  }
  return videoUrl; // Return original if no pattern matches
};
